import { useCallback, FC, useState, useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import dayjs from 'dayjs';

import { AllieButton, AllieSelect, AllieText, AllieModal } from '../../../components';

import { useOutsideAlerter } from '../../../common/hooks';
import {
  StyledDateRangeContainer,
  StyledDateRangeContent,
  StyledDateRangeFilter,
  StyledDateRangeSelect,
} from '../DateRangeFilter/DateRangeFilter.styles';
import { DateRangeFilterProps } from '../DateRangeFilter/DateRangeFilter.types';

const namespace = 'date-range-filter';

export const AllieDateRangeFilterV2: FC<DateRangeFilterProps> = ({
  selected,
  onSelect,
  staticOpts = [],
  selectedRange = [],
  dateRangePickerOpts = {},
  customRangeLabel = 'custom',
  title = 'Periodicity',
  closeText = 'Close',
  confirmText = 'Confirm',
  isMobile = false,
  placeholder,
  toLeft = false,
  position = 'below',
  'data-testid': dataTestId,
}) => {
  const [rangeEl, setRangeEl] = useState<any>();
  const [selection, setSelection] = useState<Date[]>(selectedRange);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelection(selectedRange);
  }, [selectedRange]);

  const selectionDone = useCallback(
    (value: string | Date[]) => {
      onSelect(value);
      setShowFilter(false);
      setIsModalOpen(false);
    },
    [onSelect]
  );

  // Desktop calendar visibility handlers
  const hideCalendar = useCallback(() => {
    document
      .getElementsByClassName('rdrDateRangeWrapper')[0]
      ?.setAttribute('style', 'opacity: 0; position: absolute; right: 9999px;');

    if (toLeft) {
      document
        .getElementsByClassName('rdrDateRangePickerWrapper')[0]
        ?.setAttribute('style', 'position: absolute; left: 0;');
    }
  }, [toLeft]);

  const showCalendar = useCallback(() => {
    document.getElementsByClassName('rdrDateRangeWrapper')[0]?.setAttribute('style', 'opacity: 1;');

    if (toLeft) {
      document
        .getElementsByClassName('rdrDateRangePickerWrapper')[0]
        ?.setAttribute('style', 'position: absolute; left: -300px;');
    }
  }, [toLeft]);

  const toggleCalendar = useCallback(() => {
    const wrapper = document.getElementsByClassName('rdrDateRangeWrapper')[0];
    const style = wrapper?.getAttribute('style');
    style?.includes('opacity: 0;') ? showCalendar() : hideCalendar();
  }, [showCalendar, hideCalendar]);

  const handleClose = useCallback(() => {
    hideCalendar();
    setSelection(selectedRange);
    setShowFilter(false);
    setIsModalOpen(false);
  }, [selectedRange, hideCalendar]);

  useOutsideAlerter(filterRef, handleClose);

  const selectedText = useMemo(() => {
    const targetStaticOpt = staticOpts.find((o) => o.value === selected)?.label;
    if (targetStaticOpt) return targetStaticOpt;
    if (selectedRange.length)
      return `${dayjs(selectedRange[0]).format('YYYY-MM-DD')} - ${dayjs(selectedRange[1]).format(
        'YYYY-MM-DD'
      )}`;
    return '';
  }, [selected, selectedRange, staticOpts]);

  const handleSelectChange = (e: CustomEvent) => {
    const value = e.detail.value;

    if (value === 'custom') {
      setIsModalOpen(true);
    } else {
      selectionDone(value);
    }
  };

  const mobileSelectOptions = [
    ...staticOpts.map((opt: { label: any; value: any }) => ({
      text: opt.label,
      value: opt.value,
    })),
    {
      text: customRangeLabel,
      value: 'custom',
    },
  ];

  useLayoutEffect(() => {
    if (showFilter) {
      const wrapper = document.getElementsByClassName('rdrDateRangeWrapper')[0];
      if (wrapper) {
        selected && hideCalendar();
        selectedRange.length && setSelection(selectedRange);
        setRangeEl(wrapper);
      }
    }
  }, [showFilter, selected, selectedRange]);

  type Position = 'above' | 'below' | 'below-left';
  type StyleObject = {
    position?: 'absolute';
    bottom?: string;
    right?: number | string;
  };

  const positionSelector = (selectedPosition: Position): StyleObject => {
    switch (selectedPosition) {
      case 'above':
        return { position: 'absolute', bottom: '45px' };
      case 'below-left':
        return { position: 'absolute', right: 0 };
      default:
        return {};
    }
  };

  return (
    <StyledDateRangeContainer isMobile={isMobile} data-testid={dataTestId}>
      <StyledDateRangeContent>
        {isMobile ? (
          <>
            <AllieSelect
              placeholder={placeholder}
              options={mobileSelectOptions}
              selected={selected as any}
              selectedText={selectedText}
              onChange={handleSelectChange}
              interface="popover"
            />
            <AllieModal
              isOpen={isModalOpen}
              onDidDismiss={async () => {
                handleClose();
                return Promise.resolve({});
              }}
              initialBreakpoint={0.5}
              breakpoints={[0, 0.5]}
              backdropDismiss={false}
              handle={false}
            >
              <div className="p-4">
                <StyledDateRangeFilter
                  isMobile={true}
                  onChange={(item: any) => {
                    setSelection([item.selection.startDate, item.selection.endDate]);
                  }}
                  showDateDisplay={false}
                  showPreview={false}
                  retainEndDateOnFirstSelection
                  preventSnapRefocus
                  calendarFocus="backwards"
                  months={1}
                  ranges={[
                    {
                      startDate: selection[0] || new Date(),
                      endDate: selection[1] || new Date(),
                      key: 'selection',
                    },
                  ]}
                  direction="vertical"
                  staticRanges={[]}
                  inputRanges={[]}
                  {...dateRangePickerOpts}
                />
                <div className="flex justify-end gap-2 mt-4">
                  <AllieButton
                    color="light"
                    corner="rounded-edges"
                    onclick={() => {
                      handleClose();
                      setIsModalOpen(!isModalOpen);
                    }}
                    data-testid="period-picker-close"
                  >
                    {closeText}
                  </AllieButton>
                  <AllieButton
                    corner="rounded-edges"
                    onclick={() => {
                      selectionDone(selection);
                      setIsModalOpen(!isModalOpen);
                    }}
                    data-testid="period-picker-apply"
                  >
                    {confirmText}
                  </AllieButton>
                </div>
              </div>
            </AllieModal>
          </>
        ) : (
          // Desktop view remains unchanged
          <StyledDateRangeSelect>
            <div className={`${namespace}__item`}>
              <AllieText
                className={`${namespace}__label`}
                type="body3"
                weight={400}
                color="neutral-50"
              >
                {title}
              </AllieText>
              <AllieSelect
                className={`${namespace}__item__select`}
                placeholder={placeholder}
                options={[]}
                color={{
                  backgroundColor: 'white',
                  allieColor: 'neutral-90',
                }}
                onClick={() => setShowFilter(!showFilter)}
                selectedText={selectedText}
                selected={selected as any}
                interfaceOptions={{
                  cssClass: `${namespace}__interface`,
                  arrow: false,
                  showBackdrop: false,
                }}
              />
            </div>
          </StyledDateRangeSelect>
        )}
        {/* Desktop calendar popup remains unchanged */}
        {showFilter && !isMobile && (
          <div style={positionSelector(position)} ref={filterRef}>
            <StyledDateRangeFilter
              isMobile={isMobile}
              toLeft={toLeft}
              onChange={(item: any) => {
                switch (item.selection.key) {
                  case 'selection': {
                    setSelection([item.selection.startDate, item.selection.endDate]);
                    break;
                  }
                  case 'showRange': {
                    toggleCalendar();
                    break;
                  }
                  default: {
                    hideCalendar();
                    setSelection([]);
                    selectionDone(item.selection.key);
                    break;
                  }
                }
              }}
              showDateDisplay={false}
              retainEndDateOnFirstSelection
              preventSnapRefocus
              calendarFocus={'backwards'}
              months={2}
              ranges={[
                {
                  startDate: selection[0] || new Date(),
                  endDate: selection[1] || new Date(),
                  key: 'selection',
                },
              ]}
              direction={'horizontal'}
              renderStaticRangeLabel={(range: any) => (
                <span data-testid={`period-picker-${range.range().key}`}>{range.label}</span>
              )}
              inputRanges={[]}
              staticRanges={staticOpts
                ?.map((o: { label: any; value: any; selected: any }) => ({
                  label: o.label,
                  hasCustomRendering: true,
                  range: () => ({
                    startDate: new Date(),
                    endDate: new Date(),
                    key: o.value,
                  }),
                  isSelected: () => o.selected,
                }))
                .concat([
                  {
                    label: customRangeLabel,
                    hasCustomRendering: true,
                    range: () => ({
                      startDate: selectedRange[0] || new Date(),
                      endDate: selectedRange[1] || new Date(),
                      key: 'showRange',
                    }),
                    isSelected: () => selectedRange.length > 0 && !selected,
                  },
                ])}
              {...dateRangePickerOpts}
            />

            {rangeEl &&
              ReactDOM.createPortal(
                <div style={{ textAlign: 'center', paddingBottom: 5 }}>
                  <AllieButton
                    style={{ marginRight: 7.5 }}
                    allieSize="small"
                    color={'light'}
                    corner="rounded-edges"
                    onClick={handleClose}
                    data-testid="period-picker-close"
                  >
                    {closeText}
                  </AllieButton>
                  <AllieButton
                    allieSize="small"
                    corner="rounded-edges"
                    onClick={() => selectionDone(selection)}
                    data-testid="period-picker-apply"
                  >
                    {confirmText}
                  </AllieButton>
                </div>,
                rangeEl
              )}
          </div>
        )}
      </StyledDateRangeContent>
    </StyledDateRangeContainer>
  );
};
